/* Implements Warm Academic theme styling for primary dashboard view */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, BarChart, Bar
} from 'recharts';
import { 
  Trophy, Clock, Target, 
  Zap, Stethoscope, Archive, ChevronRight, Award, 
  Flame, MapPin, Loader2, Sparkles, 
  Activity, HeartPulse,
  Timer, Microscope, Users, 
  Globe, CalendarDays, ListChecks,
  BarChart3, BrainCircuit, AlertTriangle, CheckCircle2,
  Sun, CloudSun, CloudLightning, RefreshCw,
  ShieldCheck, ArrowUpRight, TrendingUp,
  BookOpen, Layers, ClipboardList, GraduationCap, Brain, Beaker,
  ShieldAlert, TrendingDown, Medal, Info,
  Eye, HelpCircle, ListTodo, Circle, Play,
  SunMedium,
  CloudRain,
  Compass,
  ArrowRight,
  AlertCircle,
  Lock,
  ChevronUp,
  ChevronDown,
  ActivitySquare,
  FileText,
  Binary,
  Gauge,
  ScrollText,
  LayoutDashboard
} from 'lucide-react';
import { DiagnosticLaunchPad } from '../components/DiagnosticLaunchPad';
import { StreakWidget } from '../components/StreakWidget';
import { RemediationLoop } from '../components/RemediationLoop';
import { CommunityVelocity } from '../components/CommunityVelocity';
import { GamificationService } from '../services/gamificationService';
import { useAuth } from '../components/AuthBridge';
import { StorageService } from '../services/storageService';
import { MissedQuestionsService } from '../services/missedQuestionsService';
import { AdUnit } from '../components/AdUnit';
import { StudyPlannerOverlay } from '../components/StudyPlannerOverlay';
import { SponsoredService } from '../services/sponsoredService';
import { predictReadiness } from '../services/geminiService';
import { RegistryCoverageWidget } from '../components/RegistryCoverageWidget';
import { MilestoneRibbon } from '../components/MilestoneRibbon';
import { ReadinessCertificate } from '../components/ReadinessCertificate';
import { FlashcardSRSMetadata } from '../types';
import { ModuleHeader } from '../components/ModuleHeader';
import { AdvanceSuggestionPanel } from '../components/AdvanceSuggestionPanel';
import { ClinicalTrustDashboard } from '../components/ClinicalTrustDashboard';
import { WarmAcademicDashboard } from '../components/WarmAcademicDashboard';
import { StructuredCourseDashboard } from '../components/StructuredCourseDashboard';
import { ContinueLastQuizCard } from '../components/ContinueLastQuizCard';
import { MiniPlayer } from '../components/MiniPlayer';
import { US_STATES } from '../data/mockData';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { useParams } from 'react-router';
import { PageLoader } from '../components/PageLoader';

export const Dashboard: React.FC<{ onNavigate: (page: string) => void }> = ({ onNavigate }) => {
  const tokens = useThemeTokens();
  const { certId: paramCertId } = useParams<{ certId?: string }>();
  const { user, activeCert, isLoading: authLoading, updateUser } = useAuth();
  const [missedCount, setMissedCount] = useState(MissedQuestionsService.getCount());
  const [isHydrated, setIsHydrated] = useState(false);
  const [showPlanner, setShowPlanner] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [isForecasting, setIsForecasting] = useState(false);
  const [momentum, setMomentum] = useState<any[]>([]);
  const [config, setConfig] = useState(SponsoredService.getConfig());
  const [showDetailedAnalytics, setShowDetailedAnalytics] = useState(false);
  const [showAdvisory, setShowAdvisory] = useState(false);

  useEffect(() => {
      const handleUpdate = () => {
          setConfig(SponsoredService.getConfig());
      };
      window.addEventListener('sponsored-config-updated', handleUpdate);
      return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);

  const [standingData, setStandingData] = useState<any>(null);
  const [isStandingLoading, setIsStandingLoading] = useState(true);
  
  // Registry Coverage States
  const [registryTotals, setRegistryTotals] = useState<any>(null);
  const [isRegistryLoading, setIsRegistryLoading] = useState(true);

  const isPro = user?.planTier === 'paid';

  const [selectedOnboardingState, setSelectedOnboardingState] = useState(user?.state || 'Global');
  const [isUpdatingState, setIsUpdatingState] = useState(false);
  const [stateUpdatedSuccessfully, setStateUpdatedSuccessfully] = useState(false);
  const [temporaryForceDashboard, setTemporaryForceDashboard] = useState(false);

  useEffect(() => {
    if (user?.state) {
      setSelectedOnboardingState(user.state);
    }
  }, [user?.state]);

  const handleOnboardingStateChange = async (newState: string) => {
    setSelectedOnboardingState(newState);
    setIsUpdatingState(true);
    setStateUpdatedSuccessfully(false);
    try {
      await updateUser({
        state: newState,
        stateManuallySet: true
      });
      setStateUpdatedSuccessfully(true);
      setTimeout(() => setStateUpdatedSuccessfully(false), 3000);
    } catch (e) {
      console.error("Failed to update onboarding state", e);
    } finally {
      setIsUpdatingState(false);
    }
  };

  const isOnboardingMode = useMemo(() => {
    if (!config?.onboardingFeatureEnabled) return false;
    if (!user) return false;
    if (user.role === 'admin') return false; 

    const thresholdHours = config?.onboardingHoursThreshold ?? 48;
    const registrationTime = user.createdAt ? new Date(user.createdAt).getTime() : Date.now();
    const msecElapsed = Date.now() - registrationTime;
    const hoursElapsed = msecElapsed / (1000 * 60 * 60);
    const isNewUser = hoursElapsed <= thresholdHours;

    const hasNoQuizzes = !user?.stats?.quizzesTaken || user.stats.quizzesTaken === 0;
    const hasEmptyHistory = !user?.history || user.history.length === 0;

    return isNewUser && (hasNoQuizzes || hasEmptyHistory);
  }, [user, config?.onboardingFeatureEnabled, config?.onboardingHoursThreshold]);

  const getCardBgClass = (colorKey: 'indigo' | 'sky' | 'teal' | 'emerald' | 'rose' | 'amber' | 'violet' | 'purple') => {
    const isVibrant = config?.enableVibrantCards !== false;
    if (!isVibrant) {
      return 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800/80';
    }
    switch (colorKey) {
      case 'indigo':
        return 'bg-gradient-to-br from-indigo-50/70 via-indigo-50/30 to-indigo-100/10 border-indigo-200/80 dark:from-slate-900/90 dark:via-indigo-950/35 dark:to-indigo-950/55 dark:border-indigo-800/60 shadow-[0_4px_20px_-4px_rgba(99,102,241,0.08)]';
      case 'sky':
        return 'bg-gradient-to-br from-sky-50/70 via-sky-50/30 to-sky-100/10 border-sky-200/80 dark:from-slate-900/90 dark:via-sky-950/35 dark:to-sky-950/55 dark:border-sky-800/60 shadow-[0_4px_20px_-4px_rgba(14,165,233,0.08)]';
      case 'teal':
        return 'bg-gradient-to-br from-teal-50/70 via-teal-50/30 to-teal-100/10 border-teal-200/80 dark:from-slate-900/90 dark:via-teal-950/35 dark:to-teal-950/55 dark:border-teal-800/60 shadow-[0_4px_20px_-4px_rgba(20,184,166,0.08)]';
      case 'emerald':
        return 'bg-gradient-to-br from-emerald-50/70 via-emerald-50/30 to-emerald-100/10 border-emerald-200/80 dark:from-slate-900/90 dark:via-emerald-950/35 dark:to-emerald-950/55 dark:border-emerald-800/60 shadow-[0_4px_20px_-4px_rgba(16,185,129,0.08)]';
      case 'rose':
        return 'bg-gradient-to-br from-rose-50/70 via-rose-50/30 to-rose-100/10 border-rose-200/80 dark:from-slate-900/90 dark:via-rose-950/35 dark:to-rose-950/55 dark:border-rose-800/60 shadow-[0_4px_20px_-4px_rgba(244,63,94,0.08)]';
      case 'amber':
        return 'bg-gradient-to-br from-amber-50/70 via-amber-50/30 to-amber-100/10 border-amber-200/80 dark:from-slate-900/90 dark:via-amber-950/35 dark:to-amber-950/55 dark:border-amber-800/60 shadow-[0_4px_20px_-4px_rgba(245,158,11,0.08)]';
      case 'violet':
        return 'bg-gradient-to-br from-violet-50/70 via-violet-50/30 to-violet-100/10 border-violet-200/80 dark:from-slate-900/90 dark:via-violet-950/35 dark:to-violet-950/55 dark:border-violet-800/60 shadow-[0_4px_20px_-4px_rgba(139,92,246,0.08)]';
      case 'purple':
        return 'bg-gradient-to-br from-purple-50/70 via-purple-50/30 to-purple-100/10 border-purple-200/80 dark:from-slate-900/90 dark:via-purple-950/35 dark:to-purple-950/55 dark:border-purple-800/60 shadow-[0_4px_20px_-4px_rgba(168,85,247,0.08)]';
      default:
        return 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800/80';
    }
  };

  // --- SRS CALCULATIONS ---
  const srsDueCount = useMemo(() => {
    const srsMap = user?.flashcardSRS || {};
    const now = new Date();
    return Object.values(srsMap).filter((m: any) => new Date(m.nextReview) <= now).length;
  }, [user?.flashcardSRS]);

  useEffect(() => {
    const events = [
        { name: "Jessica R.", state: "TX", action: "Qualified in Simulation", icon: Award, color: "text-emerald-500" },
        { name: "Marcus L.", state: "FL", action: "Cleared Mistake Vault", icon: Archive, color: "text-rose-500" },
        { name: "Sarah W.", state: "CA", action: "Earned Safety Badge", icon: Sparkles, color: "text-indigo-500" },
        { name: "James K.", state: "NY", action: "Completed Math Lab", icon: Target, color: "text-amber-500" },
        { name: "Elena G.", state: "GA", action: "Hit 7-Day Streak", icon: Flame, color: "text-orange-500" }
    ];
    setMomentum(events);
    
    const interval = setInterval(() => {
        setMomentum(prev => {
            const next = [...prev];
            const item = next.pop();
            if (item) next.unshift(item);
            return next;
        });
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsHydrated(true), 250);
    
    const handleMissedUpdate = (e: any) => {
        setMissedCount(e.detail?.count ?? MissedQuestionsService.getCount());
    };

    const fetchRegistryData = async () => {
        if (!activeCert?.id) return;
        setIsRegistryLoading(true);
        try {
            const data = await StorageService.getRegistrySummary(activeCert.id);
            setRegistryTotals(data);
        } catch (e) {
            console.warn("Registry Telemetry Offline");
        } finally {
            setIsRegistryLoading(false);
        }
    };

    const fetchStanding = async () => {
        if (!user?.id || !activeCert?.id) return;
        setIsStandingLoading(true);
        try {
            const data = await StorageService.getLeaderboardData(activeCert.id, user.id, user.state || 'Global');
            setStandingData(data);
        } catch (e) {
            console.warn("Standing Engine Offline");
        } finally {
            setIsStandingLoading(false);
        }
    };

    fetchRegistryData();
    fetchStanding();

    // Streak Update Logic
    if (user && config.isStreaksEnabled) {
      const { streak, updated } = GamificationService.calculateStreak(user.lastActiveDate, user.streakCount);
      if (updated) {
        updateUser({
          streakCount: streak,
          lastActiveDate: new Date().toISOString()
        }).catch(err => console.error("Failed to update streak", err));
      }
    }

    window.addEventListener('missed-questions-updated', handleMissedUpdate);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('missed-questions-updated', handleMissedUpdate);
    };
  }, [user?.id, activeCert?.id, user?.state]);

  const analytics = useMemo(() => {
    const history = (user?.history || []).filter(h => h.certId === activeCert?.id);
    
    if (history.length === 0) return { 
        readiness: 0, streak: 0, sessions: 0, focusArea: 'Study Hub Calibration', 
        masteryTrend: [], timeDistribution: [], domainProficiency: [],
        burnDown: [], metacognition: { danger: 0, mastered: 0, honest: 0, guess: 0 },
        totalItemsProcessed: 0
    };

    const trend = [...history].reverse().slice(-10).map((h, i) => ({
        index: i + 1,
        score: h.score,
        date: h.date
    }));

    const avgScore = Math.round(history.reduce((acc, curr) => acc + curr.score, 0) / history.length);
    const passRate = Math.round((history.filter(h => h.passed).length / history.length) * 100);
    const historyReadiness = Math.round((avgScore * 0.6) + (passRate * 0.4));
    
    const readiness = history.length > 0 ? historyReadiness : (user?.readinessForecast?.predictedScore || 0);

    const domainMap: Record<string, { total: number, count: number, highest: number }> = {};
    const metacognition = { danger: 0, mastered: 0, honest: 0, guess: 0 };
    let totalItemsTracked = 0;

    history.forEach(session => {
        const breakdown = session.categoryBreakdown || session.masteryByDomain;
        if (breakdown) {
            breakdown.forEach((d: any) => {
                if (!domainMap[d.name]) domainMap[d.name] = { total: 0, count: 0, highest: 0 };
                domainMap[d.name].total += d.score;
                domainMap[d.name].count += 1;
                domainMap[d.name].highest = Math.max(domainMap[d.name].highest, d.score);
            });
        }

        const questions = session.questions || [];
        const userAnswers = session.userAnswers || {};
        const confMap = session.confidenceMap || {};

        questions.forEach((q: any) => {
            totalItemsTracked++;
            const userAns = userAnswers[q._id];
            const correctOptionObj = q.options?.find((o: any) => 
               o.isCorrect === true || String(o.isCorrect).toLowerCase() === 'true' || Number(o.isCorrect) === 1
            );
            const correctOption = correctOptionObj?._id;
            
            const isCorrect = q.type === 'ordering' 
                ? (userAns === q.options?.map((o: any) => o._id).join(','))
                : (String(userAns) === String(correctOption));
            
            const conf = confMap[q._id] || 'high';

            if (isCorrect && conf === 'high') metacognition.mastered++;
            else if (isCorrect && conf === 'low') metacognition.honest++;
            else if (!isCorrect && conf === 'high') metacognition.danger++;
            else if (!isCorrect && conf === 'low') metacognition.guess++;
        });
    });

    const domainProficiency = Object.entries(domainMap).map(([name, data]) => ({
        name: name.replace(' Skills', '').replace(' Procedures', ''),
        fullName: name,
        score: Math.round(data.total / data.count)
    })).sort((a, b) => a.score - b.score);

    const timeDistribution = [
        { name: 'Theory', value: history.filter(h => h.examMode === 'practice' || !h.examMode).length, color: '#6366f1' },
        { name: 'Audit', value: history.filter(h => h.examMode === 'standard').length, color: '#0ea5e9' },
        { name: 'Lab', value: Math.max(1, (user?.stats?.studyHours || 0) * 2), color: '#10b981' }
    ].filter(v => v.value > 0);

    const focusArea = domainProficiency.length > 0 
        ? domainProficiency[0].fullName 
        : 'General Review';

    const burnDown = history.slice().reverse().reduce((acc: any[], curr: any, idx: number) => {
        const prevTotal = idx > 0 ? acc[idx-1].total : 0;
        acc.push({
            day: curr.date,
            total: prevTotal + (curr.totalQuestions || 0)
        });
        return acc;
    }, []);

    const metaPercentages = {
        mastered: totalItemsTracked ? Math.round((metacognition.mastered / totalItemsTracked) * 100) : 0,
        honest: totalItemsTracked ? Math.round((metacognition.honest / totalItemsTracked) * 100) : 0,
        danger: totalItemsTracked ? Math.round((metacognition.danger / totalItemsTracked) * 100) : 0,
        guess: totalItemsTracked ? Math.round((metacognition.guess / totalItemsTracked) * 100) : 0
    };

    return {
        readiness,
        streak: new Set(history.map((h: any) => h.date)).size,
        sessions: history.length,
        focusArea,
        masteryTrend: trend,
        timeDistribution,
        domainProficiency: domainProficiency.slice(0, 5),
        burnDown,
        metacognition: metaPercentages,
        totalItemsProcessed: totalItemsTracked
    };
  }, [user, activeCert]);

  // --- REGISTRY COVERAGE MATRIX ---
  const coverage = useMemo(() => {
    if (!registryTotals) return null;
    
    const history = (user?.history || []).filter(h => h.certId === activeCert?.id);
    const certPrefix = activeCert?.id || 'cna';
    
    // 1. Unique questions attempted
    const seenQs = new Set<string>();
    history.forEach(session => {
        if (session.userAnswers) {
            Object.keys(session.userAnswers).forEach(qId => {
                const realId = qId.split('_prac_')[0].split('_inst_')[0].split('_gen_')[0].split('_ai_')[0].split('_skill_')[0];
                seenQs.add(realId);
            });
        }
    });

    // 2. Completed Modules
    const savedMods = localStorage.getItem(`${certPrefix}_completed_study_modules`);
    const completedModules = savedMods ? JSON.parse(savedMods).length : 0;

    // 3. Completed Skills
    const savedSkills = localStorage.getItem(`${certPrefix}_completed_skills`);
    const completedSkills = savedSkills ? JSON.parse(savedSkills).length : 0;

    // 4. Flashcards Mastered (SRS Level 5 only)
    const srsCards = user?.flashcardSRS 
        ? Object.values(user.flashcardSRS).filter((m: any) => m.level >= 5).length 
        : 0;

    // 5. Scenarios Attempted
    const scenarioHistory = history.filter(h => h.examMode === 'Scenario Quiz' || h.examMode === 'Sequential Scenario');
    const seenScenarios = new Set(scenarioHistory.map(h => h.id.split('_')[0]));

    const partitions = {
        modules: { id: 'guide', label: "Theory Deck", current: completedModules, total: registryTotals.modules, icon: BookOpen, color: "indigo" },
        questions: { id: 'study', label: "Practice Bank", current: seenQs.size, total: registryTotals.questions, icon: FileText, color: "medical" },
        skills: { id: 'skills', label: "Skill Lab", current: completedSkills, total: registryTotals.skills, icon: Layers, color: "emerald" },
        scenarios: { id: 'scenario', label: "Scenario Lab", current: seenScenarios.size, total: registryTotals.scenarios, icon: Stethoscope, color: "rose" },
        flashcards: { id: 'flashcards', label: "Recall Mastery", current: srsCards, total: registryTotals.flashcards, icon: Zap, color: "amber" }
    };

    const totalCurr = Object.values(partitions).reduce((a, b) => a + b.current, 0);
    const totalMax = Object.values(partitions).reduce((a, b) => a + b.total, 0);
    const saturation = totalMax > 0 ? Math.round((totalCurr / totalMax) * 100) : 0;

    const sortedPartitions = Object.values(partitions).sort((a, b) => {
        const pA = a.current / Math.max(1, a.total);
        const pB = b.current / Math.max(1, b.total);
        return pA - pB;
    });

    const primaryDirective = sortedPartitions[0];

    return {
        partitions,
        saturation,
        directive: primaryDirective,
        peerComparison: Math.round(saturation * 0.85)
    };
  }, [registryTotals, user, activeCert]);

  const handleRefreshForecast = useCallback(async () => {
      if (!user?.history?.length) {
          alert("Simulation data insufficient. Complete more practice exams to prime the predictive engine.");
          return;
      }
      
      setIsForecasting(true);
      try {
          let diffDays = 30; 
          if (user.studyPlan?.targetDate) {
            const target = new Date(user.studyPlan.targetDate);
            const diffTime = Math.abs(target.getTime() - Date.now());
            diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          }
          
          const forecast = await predictReadiness(
              user.history, 
              user.stats?.studyHours || 0, 
              diffDays, 
              activeCert?.name || 'CNA'
          );
          
          await updateUser({
              readinessForecast: {
                  ...forecast,
                  lastRun: new Date().toISOString()
              }
          });
      } catch (e) {
          console.error("Forecast Error:", e);
      } finally {
          setIsForecasting(false);
      }
  }, [user, activeCert, updateUser]);

  const studyPlanProgress = useMemo(() => {
    const tasks = user?.studyPlan?.tasks || [];
    if (tasks.length === 0) return 0;
    const completed = tasks.filter((t: any) => t.completed).length;
    return Math.round((completed / tasks.length) * 100);
  }, [user?.studyPlan]);

  const fullStateName = useMemo(() => {
    const stateObj = US_STATES.find(s => s.code === user?.state);
    return stateObj ? stateObj.name : (user?.state || 'Global');
  }, [user?.state]);

  const [forceHideDiagnostic, setForceHideDiagnostic] = useState(() => {
    return localStorage.getItem('elite_diagnostic_completed') === 'true';
  });

  const handleDiagnosticComplete = async (report: any) => {
    setForceHideDiagnostic(true);
    localStorage.setItem('elite_diagnostic_completed', 'true');
    try {
      const isSkipped = !!report.skipped;
      
      // Update user with completion status and forecast data
      await updateUser({
        hasCompletedDiagnostic: true,
        readinessForecast: {
          predictedScore: report.readinessScore || 0,
          weatherStatus: report.weatherStatus || 'clouds',
          rationale: report.diagnosticAudit || (isSkipped ? 'Assessment skipped. Dashboard initialized with baseline data.' : 'Assessment complete.'),
          lastRun: new Date().toISOString(),
          isDiagnostic: !isSkipped,
          skipped: isSkipped
        }
      });
      console.log("Diagnostic status saved successfully.");
    } catch (err) {
      console.error("Failed to save diagnostic results", err);
      // Fallback: Reload might help if state is out of sync
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  if (authLoading) {
    return <PageLoader message="Synchronizing Hub Analytics..." />;
  }

  if (isOnboardingMode && !temporaryForceDashboard) {
    const selectedStateRecord = US_STATES.find(s => s.code === selectedOnboardingState) || US_STATES[0];
    const currentCertCode = (paramCertId || activeCert?.id || user?.activeCertId || 'cna').toLowerCase();
    const certShort = (
      (activeCert && activeCert.id.toLowerCase() === currentCertCode ? activeCert.shortName : null) ||
      currentCertCode.toUpperCase()
    );
    const certFullName = (
      (activeCert && activeCert.id.toLowerCase() === currentCertCode ? activeCert.name : null) ||
      `${certShort} Certification`
    );
    
    return (
      <div className="max-w-3xl mx-auto py-8 md:py-12 space-y-10 animate-fade-in text-slate-900 dark:text-white pb-32">
         {/* Warm Welcome and Mission Header */}
         <div className="text-center space-y-4">
           <div className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest animate-pulse border ${tokens.badge}`}>
             <Sparkles size={11} className={tokens.accentText} />
             Personalized Onboarding Launchpad
           </div>
           <h1 className={`text-3xl md:text-5xl ${tokens.headerFont || 'font-black'} tracking-tight leading-none`}>
             Let's Kickstart Your <span className={`${tokens.accentText} uppercase tracking-tight`}>{certShort} Success</span>
           </h1>
           <p className="text-sm text-slate-500 dark:text-slate-400 max-w-lg mx-auto font-semibold">
             You are moments away from mastering the {certFullName} curriculum. Confirm your location and complete your 5-minute baseline drill to begin.
           </p>
         </div>

         {/* Steps Hub */}
         <div className={`rounded-[2.5rem] p-6 md:p-10 space-y-8 relative overflow-hidden transition-all ${tokens.cardBg}`}>
           
           {/* Step 1: State Selection */}
           <div className="space-y-4">
             <div className="flex items-start gap-4">
               <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm shrink-0 border ${tokens.badge}`}>
                 1
               </div>
               <div className="space-y-1">
                 <h3 className={`font-extrabold text-lg tracking-tight ${tokens.headerFont || 'text-slate-900 dark:text-white'}`}>Select your Testing State</h3>
                 <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">
                   Questions and learning content are automatically tailored to your selected state's curriculum and standard board requirements.
                 </p>
               </div>
             </div>

             <div className="sm:ml-12 max-w-md space-y-3">
               <div className="relative">
                 <select
                   value={selectedOnboardingState}
                   onChange={(e) => handleOnboardingStateChange(e.target.value)}
                   disabled={isUpdatingState}
                   className={`w-full p-4 pr-10 bg-slate-50 dark:bg-slate-850/80 border border-slate-200 dark:border-slate-800 rounded-2xl font-bold text-sm text-slate-800 dark:text-slate-100 outline-none focus:ring-2 cursor-pointer appearance-none transition-all duration-150 ${tokens.accentBorder}`}
                 >
                   {US_STATES.map((s) => (
                     <option key={s.code} value={s.code} className="dark:bg-slate-900 font-bold">
                       {s.name}
                     </option>
                   ))}
                 </select>
                 <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-slate-400">
                   <ChevronDown size={18} />
                 </div>
               </div>

               {/* Dyn State Subtitle */}
               <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 transition-all">
                 <MapPin size={14} className={tokens.accentText} />
                 <span>
                   Active Standard: <strong className="text-slate-900 dark:text-slate-100 font-black">{selectedStateRecord.name} Syllabus</strong>
                 </span>
                 {stateUpdatedSuccessfully && (
                   <span className="ml-auto inline-flex items-center gap-1 text-emerald-500 font-black tracking-wide text-[10px] uppercase animate-fade-in">
                     <CheckCircle2 size={12} /> Config Saved
                   </span>
                 )}
               </div>
             </div>
           </div>

           {/* Divider */}
           <div className="border-t border-slate-150 dark:border-slate-800"></div>

           {/* Step 2: Diagnostic baseline launch */}
           <div className="space-y-4">
             <div className="flex items-start gap-4">
               <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm shrink-0 border ${tokens.badge}`}>
                 2
               </div>
               <div className="space-y-1">
                 <h3 className={`font-extrabold text-lg tracking-tight ${tokens.headerFont || 'text-slate-900 dark:text-white'}`}>Your First Milestone</h3>
                 <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">
                   Let's start an ultra-fast, bite-sized <strong>5-Question baseline drill</strong>. It takes less than 3 minutes, includes helpful rationales, and sets up your active roadmap dashboard with zero overwhelm!
                 </p>
               </div>
             </div>

             <div className="sm:ml-12 max-w-md pt-2 space-y-4">
               <button
                 onClick={() => onNavigate('practice?onboarding=true')}
                 className={`w-full flex items-center justify-center gap-3 py-4 ${tokens.primaryBtn} rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg hover:shadow-xl transition-all duration-150 active:scale-[0.98] group`}
               >
                 <span>Start 5-Question Baseline Drill</span>
                 <ArrowRight size={14} className="group-hover:translate-x-1.5 transition-transform" />
               </button>

               <div className="flex items-center justify-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500 font-black uppercase tracking-wider">
                 <Compass size={12} className="animate-spin duration-[4s]" />
                 <span>Guarantees Passing Confidence Score Alignment</span>
               </div>
             </div>
           </div>
         </div>

         {/* Skip Button */}
         <div className="text-center">
           <button
             onClick={() => setTemporaryForceDashboard(true)}
             className={`text-xs font-bold text-slate-400 dark:text-slate-500 hover:${tokens.accentText} hover:underline transition-all uppercase tracking-widest`}
           >
             {certShort} Veteran? Skip to Full Dashboard &rarr;
           </button>
         </div>
      </div>
    );
  }

  if (config.activeTheme === 'structured_course' || config.activeTheme === 'structured_course_platform' || tokens.isStructuredCourse) {
    return <StructuredCourseDashboard onNavigate={onNavigate} standingData={standingData} />;
  }

  if (config.activeTheme === 'clinical_trust' || tokens.isClinical) {
    return <ClinicalTrustDashboard onNavigate={onNavigate} standingData={standingData} />;
  }

  if (config.activeTheme === 'warm_academic' || tokens.isWarmAcademic) {
    return <WarmAcademicDashboard onNavigate={onNavigate} standingData={standingData} />;
  }

  return (
    <div className="space-y-6 animate-fade-in pb-32 max-w-7xl mx-auto text-slate-900 w-full">
      <div className="space-y-6">
        {user && !user.hasCompletedDiagnostic && config.isDiagnosticEnabled && activeCert?.modules?.diagnostic !== false && !forceHideDiagnostic && (
          <DiagnosticLaunchPad 
            user={user} 
            certName={activeCert?.name || 'Certification'} 
            onComplete={handleDiagnosticComplete} 
          />
        )}
        
        {config.isWeakPointRemediationEnabled && missedCount > 5 && (
          <RemediationLoop missedCount={missedCount} />
        )}
      </div>

      {showPlanner && <StudyPlannerOverlay onClose={() => setShowPlanner(false)} />}
      {showCertificate && (
        <ReadinessCertificate 
            user={user} 
            activeCert={activeCert} 
            saturation={coverage?.saturation || 0} 
            analytics={analytics} 
            onClose={() => setShowCertificate(false)} 
        />
      )}

      <div>
        <ModuleHeader 
          icon={LayoutDashboard}
          title="Dashboard"
          description="See how well you are doing and where you need to study next to get ready for your test."
          badge="Your status"
          color="medical"
        />
      </div>

      {/* Continue In-Progress Quiz / Exam Banner */}
      <ContinueLastQuizCard onNavigate={onNavigate} />
      
      {/* 1. WELCOME HERO & TELEMETRY PANEL */}
      <div>
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
          <div className={`${!isPro && config.placements?.dashboard_welcome_right?.enabled ? 'xl:col-span-8' : 'xl:col-span-12'} flex flex-col lg:flex-row justify-between items-stretch gap-6 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-[2rem] border border-slate-200 dark:border-slate-800 transition-all shadow-sm relative overflow-hidden group w-full`}>
            <div className="absolute top-0 right-0 w-80 h-80 bg-medical-500/5 blur-[100px] pointer-events-none rounded-full"></div>
            
            <div className="flex-1 w-full relative z-10 flex flex-col justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div className={`px-3 py-1 ${tokens.badge} rounded-full text-[10px] font-black flex items-center gap-1.5 shadow-sm`}>
                    <MapPin size={11} className={tokens.accentText} />
                    <span>{fullStateName} Board Certified Standard</span>
                  </div>
                  {isPro && (
                    <span className="px-3 py-1 bg-gradient-to-r from-amber-500/10 to-amber-600/10 dark:from-amber-500/5 dark:to-amber-600/5 text-amber-600 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 border border-amber-500/20 shadow-sm">
                      <Sparkles size={11} className="text-amber-500" />
                      Elite Active
                    </span>
                  )}
                  {(coverage?.saturation || 0) >= 95 && (
                    <button 
                      onClick={() => setShowCertificate(true)}
                      className="px-3 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400 rounded-full text-[9px] font-black flex items-center gap-1.5 border border-emerald-100 dark:border-emerald-900/35 shadow-xs transition-all active:scale-95"
                    >
                      <ScrollText size={11} /> Credentials Available
                    </button>
                  )}
                </div>
                <h1 className={`text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tighter leading-none ${tokens.headerFont}`}>
                  Welcome, <span className={isPro ? 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-amber-600 animate-gradient' : tokens.accentText}>{user?.name?.split(' ')[0] || 'Candidate'}</span>
                </h1>
                <p className="text-slate-500 dark:text-slate-400 font-semibold text-xs md:text-sm mt-3 flex items-center gap-2">
                  <Activity size={16} className="text-emerald-500" />
                  Connectivity: <span className="text-slate-950 dark:text-slate-100 font-extrabold">Active & Verified Secure</span>
                </p>
              </div>

              {/* Quick Action CTAs */}
              <div className="flex flex-col gap-4 mt-6">
                <div className="flex flex-wrap gap-3">
                  <ContinueLastQuizCard onNavigate={onNavigate} variant="hero-cta" />
                  <button 
                    onClick={() => onNavigate('exam')}
                    className={`flex items-center gap-2.5 px-6 py-3 ${tokens.primaryBtn} rounded-xl font-black text-[10px] uppercase tracking-wider shadow-sm transition-all active:scale-95 group/btn`}
                  >
                    <ClipboardList size={14} className="group-hover/btn:animate-pulse" />
                    <span>Simulate timed state board</span>
                  </button>
                  <button 
                    onClick={() => onNavigate('practice')}
                    className={`flex items-center gap-2.5 px-6 py-3 ${tokens.secondaryBtn} rounded-xl font-black text-[10px] uppercase tracking-wider shadow-sm transition-all active:scale-95 group/btn`}
                  >
                    <ListChecks size={14} className="group-hover/btn:animate-bounce" />
                    <span>Practice categories and drills</span>
                  </button>
                  <button 
                    onClick={() => onNavigate('analytics')}
                    className="flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-indigo-600 via-purple-650 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-xl font-black text-[10px] uppercase tracking-wider shadow-md hover:shadow-lg transition-all active:scale-95 group/analytics-btn border border-indigo-400/20"
                  >
                    <BarChart3 size={14} className="animate-pulse text-amber-300" />
                    <span className="font-extrabold text-[#fcd34d]">View Analytics PRO</span>
                    <span className="ml-1 bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded text-[8px] font-black leading-none">PRO</span>
                  </button>
                </div>
                
                <div>
                  <button 
                    onClick={() => setShowAdvisory(!showAdvisory)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-black text-[9px] uppercase tracking-wider shadow-sm transition-all active:scale-95 group/btn border self-start ${
                      showAdvisory ? tokens.primaryBtn : tokens.secondaryBtn
                    }`}
                  >
                    <Sparkles size={12} className={showAdvisory ? "animate-spin text-white" : `${tokens.accentText} animate-pulse`} />
                    <span>{showAdvisory ? 'Hide Next Best Practice Advisory' : 'Show Next Best Practice Advisory'}</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Dashboard Telemetry Metric Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:flex lg:flex-col justify-center gap-3 w-full lg:w-[260px] relative z-10 mt-6 lg:mt-0">
              <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-150 dark:border-slate-800 flex items-center gap-3 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors">
                <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl"><Target size={18}/></div>
                <div>
                  <p className="text-[8px] font-black text-slate-400 dark:text-slate-400 uppercase tracking-widest leading-none mb-1">Readiness</p>
                  <p className="text-base font-black text-slate-900 dark:text-white tabular-nums">{analytics.readiness}%</p>
                </div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-150 dark:border-slate-800 flex items-center gap-3 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors">
                <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-xl"><Clock size={18}/></div>
                <div>
                  <p className="text-[8px] font-black text-slate-400 dark:text-slate-400 uppercase tracking-widest leading-none mb-1">Study Hours Logged</p>
                  <p className="text-base font-black text-slate-900 dark:text-white tabular-nums">
                    {(() => {
                      const rawHrs = user?.stats?.studyTimeMinutes ? (user.stats.studyTimeMinutes / 60) : (user?.stats?.studyHours || 0);
                      return Number.isInteger(rawHrs) ? rawHrs : Number(rawHrs).toFixed(2);
                    })()}h
                  </p>
                </div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-150 dark:border-slate-800 flex items-center gap-3 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors">
                <div className="p-2.5 bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 rounded-xl"><Flame size={18}/></div>
                <div>
                  <p className="text-[8px] font-black text-slate-400 dark:text-slate-400 uppercase tracking-widest leading-none mb-1">Daily Streak</p>
                  <p className="text-base font-black text-slate-900 dark:text-white tabular-nums">{analytics.streak} Days</p>
                </div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-150 dark:border-slate-800 flex items-center gap-3 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors">
                <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl"><Award size={18}/></div>
                <div>
                  <p className="text-[8px] font-black text-slate-400 dark:text-slate-400 uppercase tracking-widest leading-none mb-1">Badges</p>
                  <p className="text-base font-black text-slate-900 dark:text-white tabular-nums">{user?.stats?.badges?.length || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {!isPro && config.placements?.dashboard_welcome_right?.enabled && (
            <div className="xl:col-span-4 flex flex-col justify-start">
              <AdUnit placement="dashboard_welcome_right" />
            </div>
          )}
        </div>

        {/* Collapsible Suggestion Panel */}
        {showAdvisory && (
          <div className="mt-6 animate-fade-in">
            <div className="p-6 md:p-8 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-500/5 blur-[100px] pointer-events-none rounded-full"></div>
              <AdvanceSuggestionPanel 
                 source="exam" 
                 score={analytics.readiness} 
                 moduleTitle="Personal Board Assessment" 
              />
            </div>
          </div>
        )}
      </div>

      {/* Nursing Concept Audio Summaries Mini Player */}
      <MiniPlayer variant="inline" />

      {/* Faculty Callout Section */}
      <div className="bg-amber-500/10 dark:bg-amber-950/30 rounded-2xl p-5 border border-amber-500/20 dark:border-amber-800/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <h3 className="font-black text-slate-900 dark:text-amber-200 text-sm md:text-base flex items-center gap-2">
            <Users size={18} className="text-amber-600 dark:text-amber-400" />
            Meet the faculty behind your questions
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
            Every practice item, rationale, and procedure guide is written and verified by our licensed nursing faculty.
          </p>
        </div>
        <button
          onClick={() => onNavigate('faculty')}
          className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-black text-xs tracking-wider uppercase shadow-sm transition-all active:scale-95 flex items-center gap-2 shrink-0 cursor-pointer"
        >
          <span>Meet Board Faculty</span>
          <ArrowRight size={14} />
        </button>
      </div>

      {/* 2. CLINICAL STUDY PATHWAYS HUB & CORE PROGRESS */}
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              <Compass size={22} className="text-indigo-600 dark:text-indigo-400" />
              Clinical Study Pathways & Progress
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed">Choose an interactive certified pathway to build and inspect your exam-readiness progress indices dynamically.</p>
          </div>
          <div className="px-4 py-1.5 bg-slate-900 dark:bg-slate-800 text-white dark:text-slate-100 rounded-xl text-[10px] font-black tracking-widest shrink-0 self-end md:self-auto">
            OVERALL INTENT: {coverage?.saturation || 0}% SATURATION
          </div>
        </div>

        {/* Study Options Interactive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* Path 1: Theory Guide */}
          <div 
            onClick={() => onNavigate('guide')}
            className={`group cursor-pointer p-5 rounded-2xl border hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-md transition-all flex flex-col justify-between min-h-[175px] ${getCardBgClass('indigo')}`}
          >
            <div>
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-xl group-hover:scale-105 transition-transform">
                  <BookOpen size={18} />
                </div>
                <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 font-mono tracking-tighter">
                  {Math.min(100, Math.round(((coverage?.partitions?.modules?.current || 0) / Math.max(1, coverage?.partitions?.modules?.total || registryTotals?.modules || 14)) * 100))}%
                </span>
              </div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors mt-3">
                Clinical Theory Guide
              </h3>
              <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-tight mt-1">
                Premium curriculum modules and comprehensive study material crafted by board-certified RN educators.
              </p>
            </div>
            <div className="mt-4 space-y-1.5">
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-indigo-500 h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, ((coverage?.partitions?.modules?.current || 0) / Math.max(1, coverage?.partitions?.modules?.total || registryTotals?.modules || 14)) * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Path 2: Practice Quiz */}
          <div 
            onClick={() => onNavigate('practice')}
            className={`group cursor-pointer p-5 rounded-2xl border hover:border-sky-400 dark:hover:border-sky-500 hover:shadow-md transition-all flex flex-col justify-between min-h-[175px] ${getCardBgClass('sky')}`}
          >
            <div>
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400 rounded-xl group-hover:scale-105 transition-transform">
                  <ListChecks size={18} />
                </div>
                <span className="text-[10px] font-black text-sky-600 dark:text-sky-400 font-mono tracking-tighter">
                  {Math.min(100, Math.round(((coverage?.partitions?.questions?.current || 0) / Math.max(1, coverage?.partitions?.questions?.total || registryTotals?.questions || 150)) * 100))}%
                </span>
              </div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors mt-3">
                Practice Quiz Drills
              </h3>
              <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-tight mt-1">
                Focused practice question sets completely written and peer-reviewed by nursing pedagogy specialists.
              </p>
            </div>
            <div className="mt-4 space-y-1.5">
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-sky-500 h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, ((coverage?.partitions?.questions?.current || 0) / Math.max(1, coverage?.partitions?.questions?.total || registryTotals?.questions || 150)) * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Path 3: Board Simulator */}
          <div 
            onClick={() => onNavigate('exam')}
            className={`group cursor-pointer p-5 rounded-2xl border hover:border-teal-400 dark:hover:border-teal-500 hover:shadow-md transition-all flex flex-col justify-between min-h-[175px] ${getCardBgClass('teal')}`}
          >
            <div>
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400 rounded-xl group-hover:scale-105 transition-transform">
                  <ClipboardList size={18} />
                </div>
                <span className="text-[10px] font-black text-teal-600 dark:text-teal-400 font-mono tracking-tighter bg-teal-50 dark:bg-teal-900/30 px-1.5 py-0.5 rounded">
                  {analytics.readiness}% Ready
                </span>
              </div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors mt-3">
                Board Simulation Exam
              </h3>
              <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-tight mt-1">
                Timed official board-style mock exams developed with expert nurse logical rationales.
              </p>
            </div>
            <div className="mt-4 space-y-1.5">
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-teal-500 h-full rounded-full transition-all duration-700"
                  style={{ width: `${analytics.readiness || 0}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Path 4: Procedural Skills */}
          <div 
            onClick={() => onNavigate('skills')}
            className={`group cursor-pointer p-5 rounded-2xl border hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md transition-all flex flex-col justify-between min-h-[175px] ${getCardBgClass('emerald')}`}
          >
            <div>
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-xl group-hover:scale-105 transition-transform">
                  <Layers size={18} />
                </div>
                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 font-mono tracking-tighter">
                  {Math.min(100, Math.round(((coverage?.partitions?.skills?.current || 0) / Math.max(1, coverage?.partitions?.skills?.total || registryTotals?.skills || 22)) * 100))}%
                </span>
              </div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors mt-3">
                Clinical Skills Lab
              </h3>
              <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-tight mt-1">
                A-to-Z demonstration procedures curated by clinical RN instructors with step-by-step safety guidelines.
              </p>
            </div>
            <div className="mt-4 space-y-1.5">
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-emerald-500 h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, ((coverage?.partitions?.skills?.current || 0) / Math.max(1, coverage?.partitions?.skills?.total || registryTotals?.skills || 22)) * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Path 5: Scenario Lab */}
          <div 
            onClick={() => onNavigate('scenario')}
            className={`group cursor-pointer p-5 rounded-2xl border hover:border-rose-400 dark:hover:border-rose-500 hover:shadow-md transition-all flex flex-col justify-between min-h-[175px] ${getCardBgClass('rose')}`}
          >
            <div>
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 rounded-xl group-hover:scale-105 transition-transform">
                  <Stethoscope size={18} />
                </div>
                <span className="text-[10px] font-black text-rose-600 dark:text-rose-400 font-mono tracking-tighter">
                  {Math.min(100, Math.round(((coverage?.partitions?.scenarios?.current || 0) / Math.max(1, coverage?.partitions?.scenarios?.total || registryTotals?.scenarios || 8)) * 100))}%
                </span>
              </div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors mt-3">
                Bedside Scenario Lab
              </h3>
              <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-tight mt-1">
                High-fidelity care scenarios designed by expert bedside nurse tutors for clinical judgment mastery.
              </p>
            </div>
            <div className="mt-4 space-y-1.5">
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-rose-500 h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, ((coverage?.partitions?.scenarios?.current || 0) / Math.max(1, coverage?.partitions?.scenarios?.total || registryTotals?.scenarios || 8)) * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Path 6: Recall Flashcards */}
          <div 
            onClick={() => onNavigate('flashcards')}
            className={`group cursor-pointer p-5 rounded-2xl border hover:border-amber-400 dark:hover:border-amber-500 hover:shadow-md transition-all flex flex-col justify-between min-h-[175px] ${getCardBgClass('amber')}`}
          >
            <div>
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 rounded-xl group-hover:scale-105 transition-transform">
                  <Zap size={18} />
                </div>
                <span className="text-[10px] font-black text-amber-600 dark:text-amber-400 font-mono tracking-tighter">
                  {Math.min(100, Math.round(((coverage?.partitions?.flashcards?.current || 0) / Math.max(1, coverage?.partitions?.flashcards?.total || registryTotals?.flashcards || 60)) * 100))}%
                </span>
              </div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors mt-3">
                Recall Flashcards
              </h3>
              <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-tight mt-1">
                Essential nursing terminology and vocabulary flashcard decks reviewed by certified tutors.
              </p>
            </div>
            <div className="mt-4 space-y-1.5">
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-amber-500 h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, ((coverage?.partitions?.flashcards?.current || 0) / Math.max(1, coverage?.partitions?.flashcards?.total || registryTotals?.flashcards || 60)) * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Path 7: Clinical Math */}
          <div 
            onClick={() => onNavigate('simulator')}
            className={`group cursor-pointer p-5 rounded-2xl border hover:border-violet-400 dark:hover:border-violet-500 hover:shadow-md transition-all flex flex-col justify-between min-h-[175px] ${getCardBgClass('violet')}`}
          >
            <div>
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-violet-50 dark:bg-violet-950/40 text-violet-600 dark:text-violet-400 rounded-xl group-hover:scale-105 transition-transform">
                  <Binary size={18} />
                </div>
                <span className="text-[10px] font-black text-violet-600 dark:text-violet-400 font-mono tracking-tighter bg-violet-50 dark:bg-violet-900/40 px-1.5 py-0.5 rounded">
                  Active
                </span>
              </div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors mt-3">
                Clinical Math Lab
              </h3>
              <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-tight mt-1">
                Medical dosage calculations, measurement conversions, and math formulas vetted by nursing instructors.
              </p>
            </div>
            <div className="mt-4 space-y-1.5">
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-violet-500 h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, Math.round((user?.stats?.studyHours || 0) * 12))}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Path 8: Interactive AI Tutor */}
          <div 
            onClick={() => onNavigate('tutor')}
            className={`group cursor-pointer p-5 rounded-2xl border hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-md transition-all flex flex-col justify-between min-h-[175px] ${getCardBgClass('purple')}`}
          >
            <div>
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 rounded-xl group-hover:scale-105 transition-transform">
                  <Brain size={18} />
                </div>
                <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping"></span> Live Coaching
                </span>
              </div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors mt-3">
                Interactive AI Tutor
              </h3>
              <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 leading-tight mt-1">
                Our smart virtual coach, pre-loaded with official board syllabi and bedside nursing rationales.
              </p>
            </div>
            <div className="mt-4 space-y-1.5">
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-purple-500 h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, ((user?.stats?.points || 120) / 1000) * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* 3. HIGHLIGHTED ANALYTICS PRO SPOTLIGHT */}
      <div className="py-6">
        <div className="relative group overflow-hidden rounded-[2.5rem] border border-indigo-200 dark:border-indigo-800/85 bg-gradient-to-r from-indigo-50/70 via-indigo-50/40 to-slate-50 dark:from-indigo-950/40 dark:via-slate-900/60 dark:to-indigo-950/20 p-8 shadow-md transition-all hover:shadow-lg">
          <div className="absolute right-0 top-0 -mr-16 -mt-16 w-80 h-80 rounded-full bg-gradient-to-tr from-indigo-500/10 to-purple-500/20 blur-2xl pointer-events-none group-hover:scale-110 transition-transform duration-700" />
          <div className="absolute left-1/4 bottom-0 -ml-16 -mb-16 w-60 h-60 rounded-full bg-indigo-500/5 blur-3xl pointer-events-none" />
          
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-600/10 dark:bg-indigo-500/20 rounded-full border border-indigo-105/40 dark:border-indigo-500/20 shadow-xs animate-pulse">
                <Sparkles size={12} className="text-indigo-600 dark:text-indigo-400 fill-indigo-400/10" />
                <span className="text-[9px] font-black tracking-widest uppercase text-indigo-750 dark:text-indigo-300">Premium Analytics Hub</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-tight uppercase">
                Unlock <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-600 dark:from-indigo-400 dark:via-purple-400 dark:to-amber-550 bg-clip-text text-transparent animate-gradient">Professional Student Analytics</span>
              </h2>
              <p className="text-slate-600 dark:text-slate-350 text-sm font-semibold max-w-2xl leading-relaxed">
                Take control of your learning. View your cognitive metacognition matrix, state-certified coverage milestones, peer-comparisons, and real-time answering speed versus accuracy vectors.
              </p>
            </div>
            
            <button 
              id="highlight-analytics-btn"
              onClick={() => onNavigate('analytics')}
              className="flex items-center justify-center gap-3 px-8 py-4.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-[0_8px_30px_rgba(99,102,241,0.3)] hover:-translate-y-0.5 active:scale-95 shrink-0"
            >
              <BarChart3 size={16} />
              <span>Launch Analytics Pro</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
      <AdUnit placement="dashboard_bottom" />
    </div>
  );
};

const MetaQuadrant = ({ label, val, color, desc, icon: Icon }: any) => (
    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col gap-2 hover:bg-white hover:shadow-lg transition-all group">
        <div className="flex items-center justify-between">
            <div className={`p-1.5 rounded-lg text-white ${
                color === 'emerald' ? 'bg-emerald-500' :
                color === 'indigo' ? 'bg-indigo-500' :
                color === 'amber' ? 'bg-amber-500' : 'bg-rose-500'
            }`}>
                <Icon size={14}/>
            </div>
            <span className="text-lg font-black text-slate-900 tabular-nums">{val}%</span>
        </div>
        <div>
            <p className="text-[8px] font-black text-slate-400 tracking-widest mb-1">{label}</p>
            <p className="text-[7px] font-bold text-slate-500 tracking-tight leading-tight opacity-0 group-hover:opacity-100 transition-opacity">{desc}</p>
        </div>
    </div>
);

const TelemetryMiniCard = ({ icon: Icon, label, value, color }: any) => (
    <div className="bg-slate-50 p-4 md:p-6 rounded-[1.8rem] border border-slate-200 flex flex-col items-center text-center gap-2 hover:bg-white hover:shadow-xl transition-all group cursor-default">
        <div className={`p-2.5 rounded-xl transition-all shadow-md group-hover:scale-110 ${
            color === 'medical' ? 'bg-medical-50 text-medical-600' :
            color === 'emerald' ? 'bg-emerald-50 text-emerald-600' :
            color === 'indigo' ? 'bg-indigo-50 text-indigo-600' :
            'bg-amber-50 text-amber-600'
        }`}>
            <Icon size={20} />
        </div>
        <div>
            <p className="text-[8px] font-black text-slate-400 tracking-widest leading-none mb-1.5">{label}</p>
            <p className="text-base md:text-xl font-black text-slate-900 tabular-nums">{value}</p>
        </div>
    </div>
);